/**
 * @name ThemeSplash
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Componente de splash para transições de tema com elementos visuais do clube de praia
 * @returns {JSX.Element} - JSX do componente de splash
 * @package src/components/common/effects/ThemeSplash.tsx
 */

import NightlightIcon from "@mui/icons-material/Nightlight";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Fade } from "@mui/material";
import React, { useEffect, useState } from "react";

interface ThemeSplashProps {
  show: boolean;
  isDarkMode: boolean;
  onComplete?: () => void;
}

const ThemeSplash: React.FC<ThemeSplashProps> = ({
  show,
  isDarkMode,
  onComplete,
}) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        // Aguarda o fade out antes de chamar onComplete
        setTimeout(() => {
          onComplete?.();
        }, 500); // Tempo do fade out
      }, 600); // Tempo de exibição
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show) return null;
  return (
    <Fade in={visible} timeout={{ enter: 400, exit: 500 }}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDarkMode
            ? "radial-gradient(circle at center, rgba(30, 41, 59, 0.15) 0%, rgba(15, 23, 42, 0.1) 50%, rgba(2, 6, 23, 0.2) 100%)"
            : "radial-gradient(circle at center, rgba(254, 240, 138, 0.15) 0%, rgba(251, 191, 36, 0.1) 50%, rgba(245, 158, 11, 0.2) 100%)",
          backdropFilter: "blur(20px) saturate(1.8) brightness(1.2)",
          WebkitBackdropFilter: "blur(20px) saturate(1.8) brightness(1.2)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: isDarkMode
            ? "0 8px 32px rgba(14, 165, 233, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
            : "0 8px 32px rgba(251, 191, 36, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
          pointerEvents: "none",
          opacity: visible ? 1 : 0,
          transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          // Efeito de gotas de água
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDarkMode
              ? "radial-gradient(circle at 20% 30%, rgba(14, 165, 233, 0.08) 0%, transparent 25%), radial-gradient(circle at 70% 60%, rgba(14, 165, 233, 0.05) 0%, transparent 20%), radial-gradient(circle at 40% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 30%)"
              : "radial-gradient(circle at 20% 30%, rgba(251, 191, 36, 0.1) 0%, transparent 25%), radial-gradient(circle at 70% 60%, rgba(251, 191, 36, 0.08) 0%, transparent 20%), radial-gradient(circle at 40% 80%, rgba(251, 191, 36, 0.09) 0%, transparent 30%)",
            pointerEvents: "none",
            animation: visible
              ? "waterDroplets 2s ease-in-out infinite"
              : "none",
          },
          "@keyframes waterDroplets": {
            "0%, 100%": {
              opacity: 0.15,
              transform: "scale(1)",
            },
            "50%": {
              opacity: 0.3,
              transform: "scale(1.05)",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            width: "120px",
            height: "120px",
            animation: visible
              ? "splashPulseIn 0.8s ease-out"
              : "splashPulseOut 0.5s ease-in forwards",
            "@keyframes splashPulseIn": {
              "0%": {
                transform: "scale(0.2) rotate(-180deg)",
                opacity: 0,
              },
              "20%": {
                transform: "scale(0.6) rotate(-90deg)",
                opacity: 0.3,
              },
              "60%": {
                transform: "scale(1.1) rotate(10deg)",
                opacity: 1,
              },
              "80%": {
                transform: "scale(1.05) rotate(5deg)",
                opacity: 1,
              },
              "100%": {
                transform: "scale(1) rotate(0deg)",
                opacity: 1,
              },
            },
            "@keyframes splashPulseOut": {
              "0%": {
                transform: "scale(1) rotate(0deg)",
                opacity: 1,
              },
              "50%": {
                transform: "scale(1.1) rotate(10deg)",
                opacity: 0.6,
              },
              "100%": {
                transform: "scale(0.3) rotate(90deg)",
                opacity: 0,
              },
            }, // Círculo de fundo pulsante com efeito molhado
            "&::before": {
              content: '""',
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background: isDarkMode
                ? "radial-gradient(circle, rgba(14, 165, 233, 0.1) 0%, rgba(14, 165, 233, 0.02) 70%, transparent 100%)"
                : "radial-gradient(circle, rgba(251, 191, 36, 0.12) 0%, rgba(251, 191, 36, 0.03) 70%, transparent 100%)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.05)",
              boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.08)",
              animation: visible
                ? "pulseRingIn 0.8s ease-out"
                : "pulseRingOut 0.5s ease-in",
            },
            // Ondas concêntricas com efeito cristalino
            "&::after": {
              content: '""',
              position: "absolute",
              width: "150%",
              height: "150%",
              borderRadius: "50%",
              border: isDarkMode
                ? "2px solid rgba(14, 165, 233, 0.08)"
                : "2px solid rgba(251, 191, 36, 0.1)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              boxShadow: isDarkMode
                ? "0 0 20px rgba(14, 165, 233, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                : "0 0 20px rgba(251, 191, 36, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
              animation: visible
                ? "rippleWaveIn 0.8s ease-out"
                : "rippleWaveOut 0.5s ease-in",
            },
            "@keyframes pulseRing": {
              "0%": {
                transform: "scale(0.5)",
                opacity: 0,
              },
              "50%": {
                transform: "scale(1.2)",
                opacity: 0.8,
              },
              "100%": {
                transform: "scale(2)",
                opacity: 0,
              },
            },
            "@keyframes pulseRingIn": {
              "0%": {
                transform: "scale(0.3)",
                opacity: 0,
              },
              "50%": {
                transform: "scale(1.2)",
                opacity: 0.8,
              },
              "100%": {
                transform: "scale(1.8)",
                opacity: 0.2,
              },
            },
            "@keyframes pulseRingOut": {
              "0%": {
                transform: "scale(1.8)",
                opacity: 0.2,
              },
              "100%": {
                transform: "scale(0.5)",
                opacity: 0,
              },
            },
            "@keyframes rippleWave": {
              "0%": {
                transform: "scale(0)",
                opacity: 1,
              },
              "100%": {
                transform: "scale(1)",
                opacity: 0,
              },
            },
            "@keyframes rippleWaveIn": {
              "0%": {
                transform: "scale(0)",
                opacity: 0,
              },
              "30%": {
                transform: "scale(0.5)",
                opacity: 0.8,
              },
              "100%": {
                transform: "scale(1)",
                opacity: 0,
              },
            },
            "@keyframes rippleWaveOut": {
              "0%": {
                transform: "scale(1)",
                opacity: 0,
              },
              "100%": {
                transform: "scale(0.2)",
                opacity: 0,
              },
            },
          }}
        >
          <Box
            sx={{
              fontSize: "3.5rem",
              color: isDarkMode ? "#0ea5e9" : "#fbbf24",
              filter: isDarkMode
                ? "drop-shadow(0 0 25px rgba(14, 165, 233, 0.8)) drop-shadow(0 0 50px rgba(14, 165, 233, 0.4)) drop-shadow(0 2px 10px rgba(0, 0, 0, 0.3))"
                : "drop-shadow(0 0 25px rgba(251, 191, 36, 0.8)) drop-shadow(0 0 50px rgba(251, 191, 36, 0.4)) drop-shadow(0 2px 10px rgba(0, 0, 0, 0.2))",
              zIndex: 2,
              position: "relative",
              background: isDarkMode
                ? "radial-gradient(circle, rgba(14, 165, 233, 0.05) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(251, 191, 36, 0.08) 0%, transparent 70%)",
              borderRadius: "50%",
              padding: "10px",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              border: "1px solid rgba(255, 255, 255, 0.05)",
              boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.1)",
            }}
          >
            {isDarkMode ? (
              <NightlightIcon sx={{ fontSize: "inherit" }} />
            ) : (
              <WbSunnyIcon sx={{ fontSize: "inherit" }} />
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default ThemeSplash;
